import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  getDocs,
  serverTimestamp 
} from "firebase/firestore";
import { db } from "./firebase";

// Collection name for conversations
const CONVERSATIONS_COLLECTION = "conversations";

/**
 * Save a conversation message to Firestore
 * @param {Object} message - The message object with role and content
 * @param {string} message.role - 'user' or 'bot'
 * @param {string} message.content - The message content
 * @param {string} [message.username] - Optional username
 * @returns {Promise<string>} Document ID of the saved message
 */
export const saveConversationMessage = async (message) => {
  try {
    const docRef = await addDoc(collection(db, CONVERSATIONS_COLLECTION), {
      ...message,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  } catch (error) {
    console.error("Error saving conversation message:", error);
    throw error;
  }
};

/**
 * Get recent conversations from Firestore
 * @param {number} limitCount - Number of messages to retrieve (default: 100)
 * @returns {Promise<Array>} Array of conversation messages
 */
export const getRecentConversations = async (limitCount = 100) => {
  try {
    const q = query(
      collection(db, CONVERSATIONS_COLLECTION),
      orderBy("timestamp", "desc"),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const conversations = [];
    
    querySnapshot.forEach((doc) => {
      conversations.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Sort by timestamp ascending for chronological order
    return conversations.reverse();
  } catch (error) {
    console.error("Error getting conversations:", error);
    throw error;
  }
};

/**
 * Save a complete conversation exchange (user message + bot response) as a single grouped entry
 * @param {string} userMessage - The user's message
 * @param {string} botResponse - The bot's response
 * @param {string} [username] - Optional username
 * @param {string} [userEmail] - Optional user email
 * @returns {Promise<string>} Document ID of the saved conversation exchange
 */
export const saveConversationExchange = async (userMessage, botResponse, username = null, userEmail = null) => {
  try {
    const docRef = await addDoc(collection(db, CONVERSATIONS_COLLECTION), {
      type: "conversation_exchange",
      userMessage: {
        content: userMessage,
        role: "user"
      },
      botResponse: {
        content: botResponse,
        role: "bot"
      },
      username: username || "Anonymous",
      userEmail: userEmail || null,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString()
    });
    
    return docRef.id;
  } catch (error) {
    console.error("Error saving conversation exchange:", error);
    throw error;
  }
};
